/**
 * Consumo de agora contra um limite do plano — "3 de 3 grupos".
 *
 * A barra é o SEGUNDO canal: o número vem escrito ao lado, sempre. Cor sozinha
 * não conta nada para quem olha o celular às 23h (DESIGN_SYSTEM §1.3).
 *
 * Três casos que esta tela não confunde:
 *  - **ilimitado** (`limit === -1`): não há teto para medir, então não há barra
 *    — e a palavra é "ilimitado", nunca `-1` nem espaço em branco;
 *  - **não incluso** (`limit === 0`): também sem barra, porque uma barra vazia
 *    diria "ainda cabe". `0` é um fato, não ausência de dado (invariante 8);
 *  - **exceção negociada** (`source === 'override'`): aparece NOMEADA. O limite
 *    que não bate com a tabela do plano precisa dizer por quê, senão vira
 *    "o sistema está errado" na boca do cliente.
 */
import { Badge, cn } from '@mgdlead/ui';
import { Handshake } from 'lucide-react';

import {
  EXCECAO_NEGOCIADA,
  EXCECAO_NEGOCIADA_EXPLICACAO,
  ehExcecao,
  fracaoDoUso,
  rotuloDaChave,
  rotuloDoQueSobra,
  rotuloDoUso,
  tomDoUso,
} from './billing-ui';
import type { UsageRow } from '../../lib/api';

const TOM: Record<'success' | 'warning' | 'danger' | 'muted', string> = {
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  muted: 'bg-muted',
};

export interface BarraDeUsoProps {
  row: UsageRow;
  className?: string;
}

export function BarraDeUso({ row, className }: BarraDeUsoProps) {
  const fracao = fracaoDoUso(row);
  const tom = tomDoUso(row);
  const sobra = rotuloDoQueSobra(row);
  const excecao = ehExcecao(row);

  return (
    <div className={cn('flex flex-col gap-1.5 py-2', className)}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium">{rotuloDaChave(row.key)}</span>
          {excecao ? (
            <Badge tone="info" icon={<Handshake aria-hidden="true" />} title={EXCECAO_NEGOCIADA_EXPLICACAO}>
              {EXCECAO_NEGOCIADA}
            </Badge>
          ) : null}
        </span>

        <span className="nums text-sm text-muted-foreground">
          <span className="text-foreground">{rotuloDoUso(row)}</span>
          {sobra !== null ? <span> · {sobra}</span> : null}
        </span>
      </div>

      {fracao === null ? null : (
        <div
          role="img"
          aria-label={`${rotuloDaChave(row.key)}: ${rotuloDoUso(row)}`}
          className="h-2 w-full overflow-hidden rounded-sm bg-muted"
        >
          <div className={cn('h-full rounded-sm', TOM[tom])} style={{ width: `${fracao * 100}%` }} />
        </div>
      )}

      {excecao ? (
        <p className="text-2xs text-muted-foreground">{EXCECAO_NEGOCIADA_EXPLICACAO}</p>
      ) : null}
    </div>
  );
}
